import type { AcaoCiclo, AcaoResponsavel, EscopoResponsavel, EstadoCiclo, EtapaCiclo, ParteCiclo } from "@/api/metas.service";

// Regras do ciclo de aprovação do lado da tela: o que mostrar e quais botões
// oferecer. O servidor repete todas elas — aqui só evita oferecer o que ele
// recusaria (metas/ciclos.go, `transicao`).

export const ETAPA_ROTULO: Record<EtapaCiclo, string> = {
  NA: "Sem escopo",
  CALCULO: "Cálculo",
  REVISAO: "Revisão",
  APROVACAO: "Aprovação",
  CONCLUIDA: "Concluída",
};

export const ETAPA_COR: Record<EtapaCiclo, "default" | "info" | "warning" | "primary" | "success"> = {
  NA: "default",
  CALCULO: "info",
  REVISAO: "warning",
  APROVACAO: "primary",
  CONCLUIDA: "success",
};

export const ACAO_ROTULO: Record<AcaoResponsavel, string> = {
  ADMINISTRAR: "Administrar",
  CALCULAR: "Calcular",
  REVISAR: "Revisar",
  APROVAR: "Aprovar",
  PUBLICAR: "Publicar",
  REABRIR: "Reabrir",
};

/** Rótulo do histórico, incluindo as mudanças automáticas que o servidor grava. */
export const MANIFESTACAO_ROTULO: Record<string, string> = {
  ENCAMINHAR: "Encaminhou para revisão",
  VALIDAR: "Validou",
  APROVAR: "Aprovou",
  DEVOLVER: "Devolveu",
  DIRECIONAR: "Direcionou",
  REABRIR: "Reabriu o ciclo",
  REABRIR_CALCULO: "Cálculo reaberto",
  INVALIDAR: "Invalidado",
};

const tem = (acoes: AcaoResponsavel[], acao: AcaoResponsavel) => acoes.includes(acao);

const ESCOPO_VAZIO: EscopoResponsavel = { lojas: [], categorias: [] };

const ACAO_DA_ETAPA: Partial<Record<EtapaCiclo, AcaoResponsavel>> = {
  CALCULO: "CALCULAR",
  REVISAO: "REVISAR",
  APROVACAO: "APROVAR",
};

/** Tudo o que a parte calcula, revisa ou aprova, somando o escopo de cada ação. */
export function escopoTotal(parte: ParteCiclo): EscopoResponsavel {
  const escopos = Object.values(parte.escopos);
  return {
    lojas: [...new Set(escopos.flatMap((e) => e?.lojas ?? []))],
    categorias: [...new Set(escopos.flatMap((e) => e?.categorias ?? []))],
  };
}

/** O escopo sobre o qual a parte trabalha na etapa em que está; fora delas, o total. */
export function escopoDaEtapa(parte: ParteCiclo): EscopoResponsavel {
  const acao = ACAO_DA_ETAPA[parte.etapa];
  return (acao && parte.escopos[acao]) || escopoTotal(parte);
}

const cruzam = (a: EscopoResponsavel, b: EscopoResponsavel) =>
  a.lojas.some((l) => b.lojas.includes(l)) && a.categorias.some((c) => b.categorias.includes(c));

export interface BotaoCiclo {
  acao: AcaoCiclo;
  rotulo: string;
  /** Devolver e direcionar exigem justificativa. */
  exigeObservacao: boolean;
  /** Etapas de destino oferecidas ao direcionar. */
  destinos?: EtapaCiclo[];
}

/** As ações que o usuário logado pode executar sobre o escopo `parte`. */
export function botoesDaParte(estado: EstadoCiclo, parte: ParteCiclo, userId: string): BotaoCiclo[] {
  const minha = estado.partes.find((p) => p.userId === userId);
  const dono = minha?.id === parte.id;
  const administra = tem(estado.minhasAcoes, "ADMINISTRAR");
  const botoes: BotaoCiclo[] = [];

  if (parte.etapa === "CALCULO") {
    // Quem não calcula (só revisa/aprova) tem o cálculo encaminhado por quem calcula sobre o mesmo escopo.
    const porOutro =
      !tem(parte.acoes, "CALCULAR") &&
      !!minha &&
      tem(minha.acoes, "CALCULAR") &&
      cruzam(minha.escopos.CALCULAR ?? ESCOPO_VAZIO, escopoTotal(parte));
    if ((dono && tem(parte.acoes, "CALCULAR")) || administra || porOutro) {
      botoes.push({ acao: "ENCAMINHAR", rotulo: "Encaminhar para revisão", exigeObservacao: false });
    }
  }
  if (parte.etapa === "REVISAO" && dono && tem(parte.acoes, "REVISAR")) {
    botoes.push({ acao: "VALIDAR", rotulo: "Validar escopo", exigeObservacao: false });
    botoes.push({ acao: "DEVOLVER", rotulo: "Devolver ao cálculo", exigeObservacao: true });
  }
  if (parte.etapa === "APROVACAO" && dono && tem(parte.acoes, "APROVAR")) {
    botoes.push({ acao: "APROVAR", rotulo: "Aprovar escopo", exigeObservacao: false });
    botoes.push({
      acao: "DEVOLVER",
      rotulo: tem(parte.acoes, "REVISAR") ? "Devolver à revisão" : "Devolver ao cálculo",
      exigeObservacao: true,
    });
  }
  if (administra && parte.etapa !== "NA") {
    const destinos = (["CALCULO", "REVISAO", "APROVACAO"] as EtapaCiclo[]).filter(
      (e) =>
        e !== parte.etapa &&
        (e !== "REVISAO" || tem(parte.acoes, "REVISAR")) &&
        (e !== "APROVACAO" || tem(parte.acoes, "APROVAR")),
    );
    if (destinos.length > 0) {
      botoes.push({ acao: "DIRECIONAR", rotulo: "Direcionar", exigeObservacao: true, destinos });
    }
  }
  return botoes;
}

/**
 * Se o usuário logado pode alterar valores agora. Sem fluxo ativo vale a regra
 * de sempre; com fluxo, só quem calcula, com o próprio escopo em cálculo.
 */
export function podeEditarValores(estado: EstadoCiclo | null, userId: string): boolean {
  if (!estado?.fluxoAtivo) return true;
  const minha = estado.partes.find((p) => p.userId === userId);
  return !!estado.ciclo && !!minha && tem(minha.acoes, "CALCULAR") && minha.etapa === "CALCULO";
}

/** Quantos escopos com etapa a cumprir já concluíram, e quantos há. */
export function progressoDoCiclo(partes: ParteCiclo[]) {
  const comEtapa = partes.filter((p) => p.etapa !== "NA");
  return { concluidos: comEtapa.filter((p) => p.etapa === "CONCLUIDA").length, total: comEtapa.length };
}
